import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function NotificationsManager() {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    targetType: 'all', // 'all' or 'specific'
    targetUsers: [],
    priority: 'normal', // 'low', 'normal', 'high'
    type: 'info', // 'info', 'warning', 'success', 'alert'
  });

  useEffect(() => {
    loadNotifications();
    loadUsers();
  }, []);

  const loadNotifications = async () => {
    try {
      const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
      alert('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData.filter(u => u.isActive !== false));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const notification = {
        title: formData.title,
        message: formData.message,
        targetType: formData.targetType,
        targetUsers: formData.targetType === 'specific' ? formData.targetUsers : [],
        priority: formData.priority,
        type: formData.type,
        isRead: false,
        createdAt: new Date().toISOString(),
        sentBy: 'admin',
      };

      await addDoc(collection(db, 'notifications'), notification);

      alert('Notification sent successfully! Users will see it in their app.');
      setShowModal(false);
      resetForm();
      loadNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;

    try {
      await deleteDoc(doc(db, 'notifications', id));
      alert('Notification deleted successfully!');
      loadNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Failed to delete notification');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      targetType: 'all',
      targetUsers: [],
      priority: 'normal',
      type: 'info',
    });
  };

  const handleUserSelection = (userId) => {
    setFormData(prev => ({
      ...prev,
      targetUsers: prev.targetUsers.includes(userId)
        ? prev.targetUsers.filter(id => id !== userId)
        : [...prev.targetUsers, userId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'info': return '#2196F3';
      case 'success': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'alert': return '#F44336';
      default: return '#2196F3';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'alert': return '🚨';
      default: return 'ℹ️';
    }
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Notifications Management</h2>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Send New Notification
            </button>
          </div>

          {/* Statistics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(255, 20, 147, 0.05), rgba(233, 30, 99, 0.05))',
            borderRadius: '12px'
          }}>
            <div>
              <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Total Sent
              </h4>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#FF1493' }}>
                {notifications.length}
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Active Users
              </h4>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#4CAF50' }}>
                {users.length}
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                This Week
              </h4>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#2196F3' }}>
                {notifications.filter(n => {
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return new Date(n.createdAt) > weekAgo;
                }).length}
              </p>
            </div>
          </div>

          {loading && <p>Loading notifications...</p>}

          {!loading && notifications.length === 0 && (
            <p>No notifications sent yet. Send your first notification!</p>
          )}

          {!loading && notifications.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Target</th>
                    <th>Priority</th>
                    <th>Sent Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr key={notification.id}>
                      <td>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '18px',
                          background: `${getTypeColor(notification.type)}20`,
                        }}>
                          {getTypeIcon(notification.type)}
                        </span>
                      </td>
                      <td>
                        <strong>{notification.title}</strong>
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        {notification.message.length > 100
                          ? notification.message.substring(0, 100) + '...'
                          : notification.message}
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: notification.targetType === 'all'
                            ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                            : 'linear-gradient(135deg, #2196F3, #1976D2)',
                          color: 'white'
                        }}>
                          {notification.targetType === 'all'
                            ? 'All Users'
                            : `${notification.targetUsers.length} Users`}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: notification.priority === 'high'
                            ? '#F44336'
                            : notification.priority === 'normal'
                            ? '#FF9800'
                            : '#9E9E9E',
                          color: 'white'
                        }}>
                          {notification.priority.toUpperCase()}
                        </span>
                      </td>
                      <td>{formatDate(notification.createdAt)}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="btn btn-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Templates */}
        <div className="card">
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
            Quick Templates
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <button
              onClick={() => {
                setFormData({
                  title: 'Welcome to Beti Bachao Beti Padhao',
                  message: 'Thank you for joining our mission to empower girls and women. Explore the app to learn about schemes, campaigns, and inspiring women!',
                  targetType: 'all',
                  targetUsers: [],
                  priority: 'normal',
                  type: 'success',
                });
                setShowModal(true);
              }}
              className="btn btn-secondary"
              style={{ textAlign: 'left' }}
            >
              📢 Welcome Message
            </button>
            <button
              onClick={() => {
                setFormData({
                  title: 'New Campaign Launched',
                  message: 'Check out our latest campaign for girl education in your area. Visit the Campaigns section to learn more.',
                  targetType: 'all',
                  targetUsers: [],
                  priority: 'normal',
                  type: 'info',
                });
                setShowModal(true);
              }}
              className="btn btn-secondary"
              style={{ textAlign: 'left' }}
            >
              🎯 New Campaign
            </button>
            <button
              onClick={() => {
                setFormData({
                  title: 'Important Update',
                  message: 'Please update your profile information to receive personalized content and notifications.',
                  targetType: 'all',
                  targetUsers: [],
                  priority: 'high',
                  type: 'warning',
                });
                setShowModal(true);
              }}
              className="btn btn-secondary"
              style={{ textAlign: 'left' }}
            >
              ⚠️ Important Update
            </button>
          </div>
        </div>
      </div>

      {/* Send Notification Modal */}
      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Send Notification</h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Notification Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="info">ℹ️ Info</option>
                  <option value="success">✅ Success</option>
                  <option value="warning">⚠️ Warning</option>
                  <option value="alert">🚨 Alert</option>
                </select>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter notification title"
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Enter notification message"
                  required
                  rows={4}
                  maxLength={500}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  {formData.message.length}/500 characters
                </small>
              </div>

              <div className="form-group">
                <label>Send To *</label>
                <select
                  value={formData.targetType}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value, targetUsers: [] })}
                  required
                >
                  <option value="all">All Users</option>
                  <option value="specific">Specific Users</option>
                </select>
              </div>

              {formData.targetType === 'specific' && (
                <div className="form-group">
                  <label>Select Users ({formData.targetUsers.length} selected)</label>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    padding: '12px'
                  }}>
                    {users.map(user => (
                      <div key={user.id} style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.targetUsers.includes(user.id)}
                            onChange={() => handleUserSelection(user.id)}
                            style={{ marginRight: '8px' }}
                          />
                          <span>{user.username} ({user.email || user.mobileNumber})</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Priority *</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  required
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Notification'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsManager;
